/**
 * Image compression + object-storage upload for Display Check photos.
 *
 * fileToCompressedDataUrl(file)  — legacy helper (returns a data URL). Kept for
 * fallback / preview thumbnails while an upload is in flight.
 *
 * uploadCompressedPhoto(file)    — compresses to <=1MB then POSTs to /api/files
 * and returns { id, url } that references the object in Emergent Object Storage.
 */
import api from "@/lib/api";

const MAX_BYTES = 1_000_000; // 1 MB target after compression

function approxBytesFromDataUrl(url) {
  const b64 = url.split(",")[1] || "";
  const padding = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
  return Math.floor((b64.length * 3) / 4) - padding;
}

async function compressToBlob(file, opts = {}) {
  const maxBytes = opts.maxBytes ?? MAX_BYTES;
  let maxDim = opts.maxDim ?? 1600;
  let quality = opts.quality ?? 0.82;

  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = new Image();
  img.src = dataUrl;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });

  const draw = (dim, q) =>
    new Promise((resolve) => {
      const scale = Math.min(1, dim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => resolve(b), "image/jpeg", q);
    });

  let blob = await draw(maxDim, quality);
  let guard = 0;
  while (blob && blob.size > maxBytes && guard < 10) {
    if (quality > 0.4) quality = Math.max(0.4, quality - 0.1);
    else if (maxDim > 480) maxDim = Math.round(maxDim * 0.85);
    else break;
    blob = await draw(maxDim, quality);
    guard += 1;
  }
  if (!blob) throw new Error("Compression produced no output");
  if (blob.size > maxBytes * 1.05) {
    throw new Error("Unable to optimize this photo below 1 MB. Please retake or choose another image.");
  }
  return blob;
}

/** Legacy: compress to a data-URL string (used for local preview only). */
export async function fileToCompressedDataUrl(file, opts = {}) {
  const blob = await compressToBlob(file, opts);
  return await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
}

/** Compress + upload. Returns { id, url, size, preview } — `preview` is a
 *  transient blob URL suitable for immediate <img src> until the real URL loads. */
export async function uploadCompressedPhoto(file, opts = {}) {
  const blob = await compressToBlob(file, opts);
  const preview = URL.createObjectURL(blob);
  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  const { data } = await api.post("/files", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { id: data.id, url: data.url, size: data.size, preview };
}
